// Issue #696: turn a userfile's structured `calibratability` (set by
// userFilesRoutes from calibrationService.checkFileCalibratability) into a
// chip label + tooltip lines. Same shape as calibrationBlockedReason.js so
// the wording and color choices stay in one place across the three file-card
// render sites.

/**
 * @param {object} file  UserFile from the /user-files endpoints — needs at
 *   least { calibratable, calibratability, filter, exposure_time, temperature }
 *   (the last three are used to flesh out the missing-master tooltip with the
 *   specific config the operator would need to capture/upload).
 * @returns {{ label: string, color: 'warning'|'default', tooltipLines: string[] } | null}
 *   null when the file is calibratable or when calibratability is unknown.
 */
export function missingCalibrationReason(file) {
    if (!file) return null;
    // Calibratable → existing green "Cal" chip already covers this.
    if (file.calibratable === true) return null;
    // No calibratability detail (older response shape or non-light frame) → don't render.
    const cal = file.calibratability;
    if (!cal) return null;

    const missing = [];
    const have = [];

    const addLine = (type, info) => {
        // Skip frame types this camera doesn't need BEFORE looking at `available`.
        // The server reports a non-required prerequisite as { available: true,
        // required: false } — a "not a blocker" sentinel (so the readiness AND still
        // resolves), NOT "we have this frame". Bias on a CMOS camera is the case:
        // checking `available` first would push it into `have` and the tooltip would
        // wrongly read "Have: … bias". Also tolerates a missing info object.
        if (!info || !info.required) return; // e.g. CMOS doesn't need bias; don't surface
        if (info.available) {
            have.push(type);
            return;
        }
        switch (info.reason) {
            case 'no_master_for_config':
                if (type === 'flat' && file.filter) {
                    missing.push(`flat for filter ${file.filter}`);
                } else if (type === 'dark' && file.exposure_time != null) {
                    const tempStr = file.temperature != null ? ` @ ${file.temperature}°C` : '';
                    missing.push(`dark master (${file.exposure_time}s${tempStr})`);
                } else {
                    missing.push(`${type} master`);
                }
                break;
            case 'no_temperature_consistent_master_in_window':
                if (type === 'flat') {
                    missing.push(`flat within max-age window`);
                } else {
                    const fileTemp = file.temperature != null ? `${file.temperature}°C` : 'file';
                    missing.push(`${type} matching temperature (${fileTemp})`);
                }
                break;
            case 'science_predates_all_masters':
                missing.push(`${type} valid for this capture date (file predates all masters)`);
                break;
            default:
                missing.push(`${type} master`);
        }
    };

    addLine('dark', cal.dark);
    addLine('flat', cal.flat);
    addLine('bias', cal.bias);

    if (missing.length === 0) return null;

    const tooltipLines = [`Calibration not available — missing: ${missing.join(', ')}.`];
    if (have.length > 0) {
        tooltipLines.push(`Have: ${have.join(', ')}.`);
    }

    return {
        label: 'No Cal',
        color: 'warning',
        tooltipLines,
    };
}
